import React from 'react';

export enum CountdownCancelReason {
    ComponentUnmounted = 'Component unmounted during countdown',

    /**
     * We do not support restarting the countdown yet,
     * So this error occuring needs further debugging.
     */
    CountdownRestarted = 'Restarted while counting down',
}

const BEEP_RAMP_DOWN_DURATION = 0.3; // in seconds
const LAST_BEEP_RAMP_DOWN_DURATION = 0.5; // in seconds
const BEEP_INTERVAL_DURATION = 1.0; // in seconds
const SHORT_BEEP_FREQUENCY = 600; // in Hz
const LONG_BEEP_FREQUENCY = 1000; // in Hz

/**
 * Audible countdown hook using Web Audio API for better precision.
 * It plays the beeps at 1-second intervals, then calls the callback.
 * The countdown starts from the specified duration down to 0.
 */
const useAudibleCountdown = () => {
    const [count, setCount] = React.useState(3);

    const audioContextRef = React.useRef<AudioContext | null>(null);
    const audioNodesRef = React.useRef<AudioNode[]>([]);
    const promiseRef = React.useRef<{
        resolve: () => void;
        reject: (reason?: unknown) => void;
    } | null>(null);

    const formattedCountdown = React.useMemo(() => {
        return count <= 0 ? 'GO!' : count.toString();
    }, [count]);

    const resetCountdown = React.useCallback((value: number) => {
        setCount(value);
    }, []);

    const decrementCountdown = React.useCallback(() => {
        setCount((prevCount) => prevCount - 1);
    }, []);

    const getAudioContext = React.useCallback(() => {
        if (!audioContextRef.current || audioContextRef.current.state === 'closed') {
            audioContextRef.current = new AudioContext();
        }
        return audioContextRef.current;
    }, []);

    /**
     * Creates a beep sound using Web Audio API with precise timing.
     *
     * AUDIO GRAPH SETUP:
     * OscillatorNode → GainNode → AudioContext.destination (speakers)
     *
     * The oscillator generates a sine wave tone at the specified frequency.
     * The gain node controls the volume envelope, and the destination routes
     * the audio to the speakers.
     *
     * VOLUME ENVELOPE:
     * The oscillator by default runs at gain 1 (100% volume), but we use a
     * gain node to create a fade-out effect:
     * - Start at 0.3 (30% volume) when oscillator starts
     * - Exponentially ramp down to 0.01 over rampDownDuration
     * - Set to 0 (silence) at the end of rampDownDuration
     * - Remain at 0 for the rest of intervalDuration
     *
     * TIMING PRECISION:
     * We use the audio context's high-resolution timer instead of setTimeout
     * for accurate beep intervals. The oscillator runs for the full
     * intervalDuration and triggers onended when it stops.
     *
     * TIMELINE DIAGRAMS:
     *
     * Short beep (countdown numbers "3", "2", "1" at 600Hz):
     *   Time:    0s                      0.3s                          1.0s
     *   Volume:  0.3 ───────\            0.01                          0.0
     *                        \__________________________________________
     *   Status:  [====AUDIBLE BEEP====] [======SILENT PERIOD=========]
     *   Event:                                                         onended fires →
     *                                                                  decrementCountdown()
     *
     * Long beep (final "GO!" beep at 1000Hz):
     *   Time:    0s                                                  0.5s
     *   Volume:  0.3 ─────────────────\                             0.01
     *                                  \_____________________________
     *   Status:  [==============AUDIBLE BEEP======================]
     *   Event:                                                       onended fires →
     *                                                                resolve promise
     *
     * PARAMETERS:
     * @param audioContext - The Web Audio API context
     * @param startTime - Absolute time in the audio context timeline to start the beep
     * @param frequency - The frequency of the beep in Hz
     * @param rampDownDuration - How long the volume takes to fade to silence
     * @param intervalDuration - Total duration the oscillator runs (determines when onended fires)
     * @param onEnded - Callback triggered when oscillator stops (used to decrement countdown)
     */
    const createBeep = React.useCallback(
        (
            audioContext: AudioContext,
            startTime: number,
            frequency: number,
            rampDownDuration: number,
            intervalDuration: number,
            onEnded?: () => void,
        ) => {
            const oscillator = audioContext.createOscillator();
            const gainNode = audioContext.createGain();

            oscillator.connect(gainNode);
            gainNode.connect(audioContext.destination);

            oscillator.frequency.value = frequency;
            oscillator.type = 'sine';
            if (onEnded) {
                oscillator.onended = onEnded;
            }

            gainNode.gain.setValueAtTime(0.3, startTime);
            gainNode.gain.exponentialRampToValueAtTime(0.01, startTime + rampDownDuration);
            gainNode.gain.setValueAtTime(0, startTime + rampDownDuration);

            oscillator.start(startTime);
            oscillator.stop(startTime + intervalDuration);

            audioNodesRef.current.push(oscillator, gainNode);
        },
        [],
    );

    const createShortBeep = React.useCallback(
        (audioContext: AudioContext, startTime: number, onEnded?: () => void) => {
            createBeep(
                audioContext,
                startTime,
                SHORT_BEEP_FREQUENCY,
                BEEP_RAMP_DOWN_DURATION,
                BEEP_INTERVAL_DURATION,
                onEnded,
            );
        },
        [createBeep],
    );

    const createLongBeep = React.useCallback(
        (audioContext: AudioContext, startTime: number, onEnded?: () => void) => {
            createBeep(
                audioContext,
                startTime,
                LONG_BEEP_FREQUENCY,
                LAST_BEEP_RAMP_DOWN_DURATION,
                LAST_BEEP_RAMP_DOWN_DURATION,
                onEnded,
            );
        },
        [createBeep],
    );

    const cleanupAudioNodes = React.useCallback(() => {
        audioNodesRef.current.forEach((node) => {
            try {
                node.disconnect();
            } catch {
                // do nothing
            }
            try {
                if (node instanceof OscillatorNode) {
                    node.stop();
                }
            } catch {
                // do nothing
            }
        });
        audioNodesRef.current = [];
    }, []);

    const cleanupAudioContext = React.useCallback(() => {
        if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
            audioContextRef.current.close();
        }
    }, []);

    const resolveCountdown = React.useCallback(() => {
        if (promiseRef.current) {
            promiseRef.current.resolve();
            promiseRef.current = null;
        }
        // Clean up audio nodes after countdown completes
        cleanupAudioNodes();
    }, [cleanupAudioNodes]);

    const rejectCountdown = React.useCallback(
        (error: unknown) => {
            if (promiseRef.current) {
                const errorInstance = error instanceof Error ? error : new Error(String(error));
                promiseRef.current.reject(errorInstance);
                promiseRef.current = null;
            }
            // Clean up any audio nodes that were created before the error
            cleanupAudioNodes();
        },
        [cleanupAudioNodes],
    );

    const cleanupCountdown = React.useCallback(
        (reason: CountdownCancelReason) => {
            cleanupAudioNodes();
            cleanupAudioContext();

            /**
             * Reject only if the countdown was in progress so screen recording
             * does not start. Countdown creates the promise when it starts. If
             * there is no promise, it means no countdown was active.
             */
            if (promiseRef.current) {
                rejectCountdown(new Error(reason));
            }
        },
        [cleanupAudioContext, cleanupAudioNodes, rejectCountdown],
    );

    const createCountdownPromise = React.useCallback(() => {
        return new Promise<void>((resolve, reject) => {
            promiseRef.current = { resolve, reject };
        });
    }, []);

    const waitForCountdown = React.useCallback(
        async (durationInSeconds: number = 3) => {
            // Clean up any existing countdown first
            cleanupCountdown(CountdownCancelReason.CountdownRestarted);

            // Reset countdown display
            resetCountdown(durationInSeconds);

            // Create a new promise for this countdown
            const promise = createCountdownPromise();

            try {
                // Get or create new audio context
                const audioContext = getAudioContext();

                // Slight delay to ensure scheduling accuracy
                const startTime = audioContext.currentTime + 0.1;

                // Create short beeps for each second in the countdown
                for (let i = 0; i < durationInSeconds; i++) {
                    createShortBeep(audioContext, startTime + i, decrementCountdown);
                }

                // Add a long beep at the last to signal start of recording
                createLongBeep(audioContext, startTime + durationInSeconds, resolveCountdown);
            } catch (error) {
                // Reject promise if any step fails
                rejectCountdown(error);
            }

            return promise;
        },
        [
            resetCountdown,
            getAudioContext,
            cleanupCountdown,
            createCountdownPromise,
            rejectCountdown,
            resolveCountdown,
            createLongBeep,
            createShortBeep,
            decrementCountdown,
        ],
    );

    React.useEffect(() => {
        return () => {
            cleanupCountdown(CountdownCancelReason.ComponentUnmounted);
        };
    }, [cleanupCountdown]);

    return {
        display: formattedCountdown,
        waitForCountdown,
    };
};

export default useAudibleCountdown;
